import type { DragEvent, MouseEvent, PointerEvent, ReactNode } from 'react';
import { IconButton } from '../../ui';
import { useTranslation } from 'react-i18next';
import { href, Link } from '../../app/router';
import type { Board, Project } from '../../lib/api';
import { useFavorites, useToggleFavorite } from './queries';
import { useBoardThumbnailUrl } from './thumbnails';
import { EmptyState } from '../../ui/states';
import { Badge } from '../../ui/Badge';
import { cn } from '../../ui/cn';
import { focusRing } from '../../ui/focus';
import { Icon } from '../../ui/icons';
import { Check, Folder, Star } from 'lucide-react';
import { MenuButton, useBoardMenu, useProjectMenu, WithContextMenu } from './ItemMenu';
import { useRegisterMenu } from './SelectionScope';
import { useBoardDrag, useProjectDrag } from './dnd/useDashboardDrag';

const card = 'group relative flex flex-col gap-2.5 rounded-2xl bg-chrome p-2 pb-3 shadow-float transition-[translate] duration-(--duration-base) motion-safe:hover:-translate-y-0.5 motion-safe:focus-within:-translate-y-0.5 data-[drop-active=true]:ring-2 data-[drop-active=true]:ring-accent';
/** A drop target lights up while something that may land on it is dragged over (P16-4). */
export const dropActive = 'data-[drop-active=true]:ring-2 data-[drop-active=true]:ring-accent';
const meta = 'font-mono text-xs text-fg-muted';
/** Card title (P15): Geist medium, one line, the whole card is its hit area (the link's ::after). */
const titleLink = `block truncate font-sans text-sm font-medium text-fg after:absolute after:inset-0 after:rounded-2xl after:content-[''] ${focusRing}`;

/** "3 hours ago" for the last month, then the date (board cards). */
export function relativeTime(iso: string, lng: string, now = Date.now()): string {
  const minutes = Math.round((Date.parse(iso) - now) / 60_000);
  const rtf = new Intl.RelativeTimeFormat(lng, { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  if (Math.abs(minutes) < 1440) return rtf.format(Math.round(minutes / 60), 'hour');
  if (Math.abs(minutes) < 30 * 1440) return rtf.format(Math.round(minutes / 1440), 'day');
  return new Date(iso).toLocaleDateString(lng);
}

function FavoriteButton({ board }: { board: Board }) {
  const { t } = useTranslation();
  const favorites = useFavorites();
  const toggle = useToggleFavorite();
  const on = favorites.data?.some((b) => b.id === board.id) ?? false;
  return (
    <>
      <IconButton
        pressed={on}
        label={on ? t('dashboard.unfavorite') : t('dashboard.favorite')}
        icon={<Icon icon={Star} size={18} className={on ? 'fill-current' : undefined} />}
        className="relative z-10 h-8 w-8 aria-pressed:bg-transparent aria-pressed:text-accent"
        onClick={() => toggle.mutate({ board, on: !on })}
      />
      {toggle.isError && (
        <p role="alert" className="font-sans text-xs text-danger">
          {t('dashboard.favoriteError')}
        </p>
      )}
    </>
  );
}

export type Selectable = { selected: boolean; onSelect: (mods: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }, toggle?: boolean) => void };

/** The selection side of a card or a row: its id for the page's order, and a modified click that selects (Finder-like). */
function selectionProps(board: Board, selection: Selectable | undefined) {
  return {
    'data-board-id': board.id,
    'data-selected': selection ? String(selection.selected) : undefined,
    onClickCapture: selection
      ? (e: MouseEvent) => {
          if (!(e.shiftKey || e.metaKey || e.ctrlKey) || !(e.target instanceof Element) || !e.target.closest('a')) return;
          e.preventDefault();
          e.stopPropagation();
          selection.onSelect(e);
        }
      : undefined,
  };
}

function SelectButton({ board, selection, className }: { board: Board; selection: Selectable; className?: string }) {
  const { t } = useTranslation();
  return (
    <IconButton
      label={t('dashboard.select', { title: board.title })}
      pressed={selection.selected}
      icon={<Icon icon={Check} size={16} />}
      onClick={(e) => selection.onSelect(e, true)}
      className={cn('z-10 h-7 w-7 bg-surface/90 shadow-sm ring-1 ring-border', className)}
    />
  );
}

/**
 * A board card: the whole card opens the board; ⋯, a right click or a long press opens its menu (P16-4). In a
 * selection scope, Shift- or Cmd/Ctrl-click and the checkbox select instead of opening.
 */
export function BoardCard({ board, subtitle, actions, selection }: { board: Board; subtitle?: string; actions?: ReactNode; selection?: Selectable }) {
  const { t, i18n } = useTranslation();
  const thumbnail = useBoardThumbnailUrl(board.id);
  const menu = useBoardMenu(board);
  useRegisterMenu(board.id, menu);
  const hasMenu = menu.entries.length > 1;
  const body = (
    <>
      <div data-preview className="aspect-[16/10] overflow-hidden rounded-xl bg-paper ring-1 ring-border [background-image:radial-gradient(circle,#d4d4d8_1px,transparent_1.2px)] [background-size:16px_16px]">
        {thumbnail && <img src={thumbnail} alt="" loading="lazy" decoding="async" width={640} height={400} className="size-full object-cover" />}
      </div>
      {selection && (
        <SelectButton
          board={board}
          selection={selection}
          className="absolute top-4 left-4 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 aria-pressed:opacity-100 pointer-coarse:opacity-100"
        />
      )}
      <div className="flex min-w-0 items-center justify-between gap-1 px-1">
        <Link to={href('board', { boardId: board.id })} title={board.title} className={cn(titleLink, 'min-w-0 flex-1')}>
          {board.title}
        </Link>
        {/* Shown on hover and focus, always on touch (no hover there) and while the board is a favorite. */}
        <div
          data-card-actions
          className="flex items-center opacity-0 transition-opacity duration-(--duration-fast) group-hover:opacity-100 group-focus-within:opacity-100 has-[[aria-pressed=true]]:opacity-100 pointer-coarse:opacity-100"
        >
          <FavoriteButton board={board} />
          {hasMenu && <MenuButton menu={menu} triggerLabel={t('dashboard.boardActions')} />}
        </div>
      </div>
      <div className="flex min-w-0 items-center gap-2 px-1">
        <p className={cn(meta, 'min-w-0 truncate')}>
          {subtitle ? `${subtitle} · ` : ''}
          {relativeTime(board.lastEditedAt ?? board.updatedAt, i18n.language)}
          {board.archivedAt ? ` · ${t('dashboard.archived')}` : ''}
        </p>
        {board.isTemplate && <Badge className="shrink-0">{t('boardTemplates.badge')}</Badge>}
      </div>
      {actions && <div className="relative z-10">{actions}</div>}
    </>
  );
  const drag = useBoardDrag(board);
  const props = { className: cn(card, selection?.selected && 'ring-2 ring-accent'), ...selectionProps(board, selection), ...drag };
  return (
    <>
      {hasMenu ? (
        <WithContextMenu menu={menu} element={<article {...props} />}>
          {body}
        </WithContextMenu>
      ) : (
        <article {...props}>{body}</article>
      )}
      {menu.dialogs}
    </>
  );
}

/** One board as a row of the list view (P16-4): the same menu, selection and link as its card. */
export function BoardRow({ board, projectName, ownerName, selection }: { board: Board; projectName?: string; ownerName?: string; selection?: Selectable }) {
  const { t, i18n } = useTranslation();
  const menu = useBoardMenu(board);
  useRegisterMenu(board.id, menu);
  const hasMenu = menu.entries.length > 1;
  const cell = 'px-3 py-2 align-middle';
  const drag = useBoardDrag(board);
  const props = { className: cn('group relative border-b border-border last:border-b-0 hover:bg-surface-2', selection?.selected && 'bg-accent/10'), ...selectionProps(board, selection), ...drag };
  const body = (
    <>
      <td className={cn(cell, 'w-10')}>{selection && <SelectButton board={board} selection={selection} />}</td>
      <td className={cn(cell, 'max-w-0 w-1/2')}>
        <Link to={href('board', { boardId: board.id })} title={board.title} className={cn('block truncate font-sans font-medium text-fg', focusRing)}>
          {board.title}
        </Link>
        {board.archivedAt && <span className={meta}>{t('dashboard.archived')}</span>}
      </td>
      <td className={cn(cell, meta)}>{projectName ?? ''}</td>
      <td className={cn(cell, meta, 'whitespace-nowrap')}>{relativeTime(board.lastEditedAt ?? board.updatedAt, i18n.language)}</td>
      <td className={cn(cell, meta)}>{ownerName ?? '—'}</td>
      <td className={cn(cell, 'w-12 text-right')}>
        {hasMenu && <MenuButton menu={menu} triggerLabel={t('dashboard.boardActions')} />}
        {menu.dialogs}
      </td>
    </>
  );
  return hasMenu ? (
    <WithContextMenu menu={menu} element={<tr {...props} />}>
      {body}
    </WithContextMenu>
  ) : (
    <tr {...props}>{body}</tr>
  );
}

export function ProjectCard({ project }: { project: Project }) {
  const { t } = useTranslation();
  const menu = useProjectMenu(project);
  const drag = useProjectDrag(project);
  const hasMenu = menu.entries.length > 1;
  const props = { className: card, 'data-drop-project': project.id, 'data-drop-archived': project.archivedAt ? 'true' : undefined, ...drag };
  const body = (
    <>
      <div className="flex items-start gap-3 p-1">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-surface-2 text-fg-muted">
          <Icon icon={Folder} />
        </span>
        <Link to={href('project', { projectId: project.id })} title={project.name} className={cn(titleLink, 'min-w-0 flex-1')}>
          {project.name}
          {project.clientName && <span className="block truncate font-sans text-sm font-normal text-fg-muted">{project.clientName}</span>}
        </Link>
        {hasMenu && <MenuButton menu={menu} triggerLabel={t('dashboard.projectActions')} />}
      </div>
      {project.archivedAt && <p className={meta}>{t('dashboard.archived')}</p>}
    </>
  );
  return (
    <>
      {hasMenu ? (
        <WithContextMenu menu={menu} element={<article {...props} />}>
          {body}
        </WithContextMenu>
      ) : (
        <article {...props}>{body}</article>
      )}
      {menu.dialogs}
    </>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="os-stagger grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-6">{children}</div>;
}

export function Section({
  title,
  action,
  children,
  empty,
  emptyTitle,
  emptyBody,
  emptyAction,
  drop,
  headingProps,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  empty?: boolean;
  emptyTitle?: string;
  /** What an empty section is for, under its title (P15c: every empty state explains itself). */
  emptyBody?: string;
  emptyAction?: ReactNode;
  /** Boards dropped on the section land in this project and folder (null: the project root). */
  drop?: { projectId: string; folderId: string | null; archived: boolean };
  /** Drag of the section by its heading (folders reorder). */
  headingProps?: { onPointerDown: (e: PointerEvent<HTMLElement>) => void; onDragStart: (e: DragEvent<HTMLElement>) => void };
}) {
  const { t } = useTranslation();
  return (
    <section
      aria-label={title}
      className={cn('-m-2 flex flex-col gap-4 rounded-3xl p-2', dropActive)}
      data-drop-project={drop?.projectId}
      data-drop-folder={drop ? (drop.folderId ?? '') : undefined}
      data-drop-archived={drop?.archived ? 'true' : undefined}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className={cn('font-display text-title font-normal tracking-display text-fg', headingProps && 'cursor-grab select-none active:cursor-grabbing')} {...headingProps}>
          {title}
        </h2>
        {!(empty && emptyAction) && action}
      </div>
      {empty ? <EmptyState title={emptyTitle ?? t('dashboard.empty')} body={emptyBody} action={emptyAction} /> : children}
    </section>
  );
}
